import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Alert, Spinner, Accordion, Badge, ProgressBar } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * MyDriverReviews - View reviews left by customers for the logged-in driver
 */
export default function MyDriverReviews() {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [averages, setAverages] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/driver-reviews/my-reviews');
      setReviews(response.data.reviews || []);
      setAverages(response.data.averages);
      setTotalReviews(response.data.totalReviews || 0);
    } catch (err) {
      console.error('Error loading reviews:', err);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="d-inline-flex align-items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            size={18}
            color={star <= rating ? '#ffc107' : '#e4e5e9'}
          />
        ))}
        <span className="ms-2 fw-bold">{rating}/5</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderRatingBar = (label, value) => {
    const percentage = (parseFloat(value) / 5) * 100;
    const variant = percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'danger';
    
    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between mb-1">
          <small><strong>{label}</strong></small>
          <small><strong>{value}/5</strong></small>
        </div>
        <ProgressBar now={percentage} variant={variant} />
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading your reviews...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <h2 className="mb-4">⭐ My Customer Reviews</h2>

      {totalReviews === 0 ? (
        <Alert variant="info">
          <h5>No reviews yet</h5>
          <p className="mb-0">
            Customer reviews will appear here once customers complete the review form after their journey.
          </p>
        </Alert>
      ) : (
        <>
          {/* Summary Cards */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center" style={{ backgroundColor: '#f8f9fa' }}>
                <Card.Body>
                  <h6 className="text-muted">Total Reviews</h6>
                  <h2 className="mb-0">{totalReviews}</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center" style={{ backgroundColor: '#d4edda' }}>
                <Card.Body>
                  <h6 className="text-muted">Overall Rating</h6>
                  <h2 className="mb-2">{averages.overall}</h2>
                  {renderStars(Math.round(parseFloat(averages.overall)))}
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center" style={{ backgroundColor: '#cce5ff' }}>
                <Card.Body>
                  <h6 className="text-muted">Driver Performance</h6>
                  <h2 className="mb-2">{averages.driverAverage}</h2>
                  {renderStars(Math.round(parseFloat(averages.driverAverage)))}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Detailed Averages */}
          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: '#28a745', color: 'white' }}>
              <h5 className="mb-0">📊 Performance Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6 className="mb-3">🚕 Driver Ratings</h6>
                  {renderRatingBar('😊 Helpful & Friendly', averages.helpful)}
                  {renderRatingBar('💬 Informative', averages.informative)}
                  {renderRatingBar('🚗 Car Condition', averages.carCondition)}
                </Col>
                <Col md={6}>
                  <h6 className="mb-3">📱 Platform Experience</h6>
                  {renderRatingBar('👍 Easy to Use', averages.easyToUse)}
                  {renderRatingBar('🚕 Got Taxi Successfully', averages.gotTaxi)}
                  {renderRatingBar('📞 Communication', averages.communication)}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Individual Reviews */}
          <Card>
            <Card.Header style={{ backgroundColor: '#007bff', color: 'white' }}>
              <h5 className="mb-0">📝 Customer Feedback</h5>
            </Card.Header>
            <Card.Body>
              <Accordion>
                {reviews.map((review, index) => (
                  <Accordion.Item key={review._id} eventKey={index.toString()}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between w-100 me-3">
                        <div>
                          <strong>{review.customerName}</strong>
                          <Badge bg="success" className="ms-2">{review.overallRating}/5</Badge>
                        </div>
                        <div className="text-muted">
                          <small>{formatDate(review.createdAt)}</small>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {review.jobId && (
                        <div className="mb-3 p-2 bg-light rounded">
                          <small>
                            <strong>Journey:</strong> {review.jobId.from} → {review.jobId.to}
                            {review.jobId.fareAmount && ` • £${review.jobId.fareAmount}`}
                          </small>
                        </div>
                      )}

                      <Row>
                        <Col md={6}>
                          <h6 className="mb-2">🚕 Driver Ratings</h6>
                          <div className="mb-2">
                            <small>😊 Helpful: </small>
                            {renderStars(review.driverRatings.helpful)}
                          </div>
                          <div className="mb-2">
                            <small>💬 Informative: </small>
                            {renderStars(review.driverRatings.informative)}
                          </div>
                          <div className="mb-2">
                            <small>🚗 Car Condition: </small>
                            {renderStars(review.driverRatings.carCondition)}
                          </div>
                          {review.driverComments && (
                            <div className="mt-3 p-2 bg-light rounded">
                              <small><strong>Comments:</strong></small>
                              <p className="mb-0 mt-1"><small>{review.driverComments}</small></p>
                            </div>
                          )}
                        </Col>
                        <Col md={6}>
                          <h6 className="mb-2">📱 Platform Ratings</h6>
                          <div className="mb-2">
                            <small>👍 Easy to Use: </small>
                            {renderStars(review.platformRatings.easyToUse)}
                          </div>
                          <div className="mb-2">
                            <small>🚕 Got Taxi: </small>
                            {renderStars(review.platformRatings.gotTaxi)}
                          </div>
                          <div className="mb-2">
                            <small>📞 Communication: </small>
                            {renderStars(review.platformRatings.communication)}
                          </div>
                          {review.platformComments && (
                            <div className="mt-3 p-2 bg-light rounded">
                              <small><strong>Comments:</strong></small>
                              <p className="mb-0 mt-1"><small>{review.platformComments}</small></p>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}

